import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  TableSortLabel,
  Alert,
  Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { feedAPI, horsesAPI, referenceAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows, useSkeletonAnimation } from '../components/LoadingSkeletons';

const FEED_UNIT_OPTIONS = [
  { value: 'pounds', label: 'Pounds' },
  { value: 'scoops', label: 'Scoops' },
  { value: 'cups', label: 'Cups' },
  { value: 'flakes', label: 'Flakes' },
];

const FeedPage = () => {
  const notify = useNotify();
  const [feedRequirements, setFeedRequirements] = useState([]);
  const [summary, setSummary] = useState([]);
  const [horses, setHorses] = useState([]);
  const [feedTypes, setFeedTypes] = useState([]);
  const [feedBrands, setFeedBrands] = useState([]);
  const [feedNames, setFeedNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const skeletonAnimation = useSkeletonAnimation();
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [openFeedTypeDialog, setOpenFeedTypeDialog] = useState(false);
  const [openFeedBrandDialog, setOpenFeedBrandDialog] = useState(false);
  const [openFeedNameDialog, setOpenFeedNameDialog] = useState(false);
  const [sortBy, setSortBy] = useState('horse_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    horse_id: '',
    feed_name_id: '',
    amount: '',
    unit: 'pounds',
    morning_feed: false,
    midday_feed: false,
    evening_feed: false,
    notes: '',
  });

  const [feedTypeFormData, setFeedTypeFormData] = useState({
    name: '',
    description: '',
  });

  const [feedBrandFormData, setFeedBrandFormData] = useState({
    name: '',
    feed_type_id: '',
    description: '',
  });

  const [feedNameFormData, setFeedNameFormData] = useState({
    name: '',
    feed_type_id: '',
    brand_id: '',
    description: '',
  });

  const loadData = useCallback(async () => {
    setLoadError('');
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      const [feedRes, summaryRes, horsesRes, feedTypesRes, feedBrandsRes, feedNamesRes] = await Promise.all([
        feedAPI.getAll(params),
        feedAPI.getSummary(),
        horsesAPI.getAll(),
        referenceAPI.getFeedTypes(),
        referenceAPI.getFeedBrands(),
        referenceAPI.getFeedNames()
      ]);
      setFeedRequirements(toArray(feedRes.data, 'feed requirements'));
      setSummary(toArray(summaryRes.data, 'feed summary'));
      setHorses(toArray(horsesRes.data, 'horses').filter(horse =>
        horse.owner_type !== 'reference' &&
        (horse.current_status === 'active' || horse.current_status === 'retired')
      ));
      setFeedTypes(toArray(feedTypesRes.data, 'feed types'));
      setFeedBrands(toArray(feedBrandsRes.data, 'feed brands'));
      setFeedNames(toArray(feedNamesRes.data, 'feed names'));

      // TODO: Load feed names and types once database is set up
      // setFeedNames([]);
      // setFeedTypes([]);
    } catch (error) {
      console.error('Error loading data:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no requirements".
      setLoadError(error?.response?.data?.error || 'Unable to load feed requirements. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useRefreshOnFocus(loadData);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (feed = null) => {
    if (feed) {
      setEditingFeed(feed);
      setFormData(feed);
    } else {
      setEditingFeed(null);
      setFormData({
        horse_id: '',
        feed_type: '',
        feed_name: '',
        brand: '',
        amount: '',
        unit: 'pounds',
        feedings_per_day: 2,
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFeed(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleFeedNameChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      feed_name_id: newValue?.id || ''
    }));
  };


  // Add new feed type
  const handleCreateFeedType = async () => {
    try {
      const response = await referenceAPI.createFeedType(feedTypeFormData);
      const newFeedType = response.data;
      setFeedTypes(prev => [...prev, newFeedType]);
      setFormData(prev => ({ ...prev, feed_type_id: newFeedType.id }));
      setOpenFeedTypeDialog(false);
      setFeedTypeFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Error creating feed type:', error);
      notify.error(errorMessage(error, 'Error creating feed type'));
    }
  };

  // Add new feed brand
  const handleCreateFeedBrand = async () => {
    try {
      const response = await referenceAPI.createFeedBrand(feedBrandFormData);
      const newFeedBrand = response.data;
      setFeedBrands(prev => [...prev, newFeedBrand]);
      setFormData(prev => ({ ...prev, brand_id: newFeedBrand.id }));
      setOpenFeedBrandDialog(false);
      setFeedBrandFormData({ name: '', feed_type_id: '', description: '' });
    } catch (error) {
      console.error('Error creating feed brand:', error);
      notify.error(errorMessage(error, 'Error creating feed brand'));
    }
  };

  // Add new feed name
  const handleCreateFeedName = async () => {
    try {
      // Prepare data - exclude brand_id if feed type is Hay or if brand_id is empty
      const selectedFeedType = feedTypes.find((t) => t.id === feedNameFormData.feed_type_id);
      const isHay = selectedFeedType?.name === 'Hay';
      const submitData = {
        name: feedNameFormData.name,
        feed_type_id: feedNameFormData.feed_type_id,
        description: feedNameFormData.description
      };
      
      // Only include brand_id if it has a valid value and feed type is not Hay
      if (!isHay && feedNameFormData.brand_id && feedNameFormData.brand_id !== '') {
        submitData.brand_id = feedNameFormData.brand_id;
      }
      
      const response = await referenceAPI.createFeedName(submitData);
      const newFeedName = response.data;
      setFeedNames(prev => [...prev, newFeedName]);
      setFormData(prev => ({ ...prev, feed_name_id: newFeedName.id }));
      setOpenFeedNameDialog(false);
      setFeedNameFormData({ name: '', feed_type_id: '', brand_id: '', description: '' });
    } catch (error) {
      console.error('Error creating feed name:', error);
      notify.error(errorMessage(error, 'Error creating feed name'));
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingFeed) {
        await feedAPI.update(editingFeed.id, formData);
      } else {
        await feedAPI.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving feed requirement:', error);
      notify.error(errorMessage(error, 'Error saving feed requirement'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await feedAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting feed requirement:', error);
      notify.error(errorMessage(error, 'Error deleting feed requirement'));
    }
  };

  const pluralize = (count, singular, plural = null) => {
    if (count === 1) {
      return singular;
    }
    return plural || `${singular}s`;
  };

  const formatUnit = (amount, unit) => {
    const unitLower = (unit || '').toLowerCase();
    let unitSingular = unitLower;
    let unitPlural = unitLower;
    
    // Handle common unit pluralizations
    if (unitLower === 'flakes') {
      unitSingular = 'flake';
      unitPlural = 'flakes';
    } else if (unitLower === 'flake') {
      unitSingular = 'flake';
      unitPlural = 'flakes';
    } else if (unitLower === 'scoops') {
      unitSingular = 'scoop';
      unitPlural = 'scoops';
    } else if (unitLower === 'scoop') {
      unitSingular = 'scoop';
      unitPlural = 'scoops';
    } else if (unitLower === 'cups') {
      unitSingular = 'cup';
      unitPlural = 'cups';
    } else if (unitLower === 'cup') {
      unitSingular = 'cup';
      unitPlural = 'cups';
    } else if (unitLower === 'pounds') {
      unitSingular = 'pound';
      unitPlural = 'pounds';
    } else if (unitLower === 'pound') {
      unitSingular = 'pound';
      unitPlural = 'pounds';
    }
    
    return pluralize(amount, unitSingular, unitPlural);
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h4">Feed Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Feed Requirement
          </Button>
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          {/* Holds one row of summary cards during the first load, so the table below
              doesn't start high and get pushed down a row when they arrive. */}
          {loading && [0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={`skeleton-${i}`}>
              <Card aria-hidden="true">
                <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                  <Skeleton animation={skeletonAnimation} width="60%" height={28} />
                  <Skeleton animation={skeletonAnimation} width="45%" />
                  <Skeleton animation={skeletonAnimation} width="30%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
          {summary.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent sx={{ padding: 1, '&:last-child': { paddingBottom: 1 } }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontSize: '1rem' }}>{item.feed_name || 'Generic'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.25 }}>
                    {item.total_daily_amount} {formatUnit(item.total_daily_amount, item.unit)} daily
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.horse_count} {pluralize(item.horse_count, 'horse', 'horses')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {loadError ? (
          <Alert severity="error" sx={{ mb: 0.5 }}>
            {loadError}
          </Alert>
        ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'horse_name'}
                  direction={sortBy === 'horse_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('horse_name')}
                >
                  Horse
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'feed_type'}
                  direction={sortBy === 'feed_type' ? sortOrder : 'asc'}
                  onClick={() => handleSort('feed_type')}
                >
                  Feed Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'feed_name'}
                  direction={sortBy === 'feed_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('feed_name')}
                >
                  Feed Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'amount'}
                  direction={sortBy === 'amount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'feedings_per_day'}
                  direction={sortBy === 'feedings_per_day' ? sortOrder : 'asc'}
                  onClick={() => handleSort('feedings_per_day')}
                >
                  Feedings per Day
                </TableSortLabel>
              </TableCell>
              <TableCell>Daily Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={7} />}
            {feedRequirements.map((feed) => (
              <TableRow key={feed.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(feed) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(feed.id), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{feed.horse_name}</TableCell>
                <TableCell>{feed.feed_type}</TableCell>
                <TableCell>{feed.feed_name || '-'}</TableCell>
                <TableCell>
                  {feed.amount} {feed.unit}
                </TableCell>
                <TableCell>{feed.feedings_per_day}</TableCell>
                <TableCell>
                  {feed.daily_amount} {feed.unit}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFeed ? 'Edit Feed Requirement' : 'Add New Feed'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Horse Selection */}
            <Autocomplete
              options={horses}
              getOptionLabel={(option) => option.name || option.call_name || 'Unknown'}
              filterOptions={createPrefixFilter((option) => option.name || option.call_name || 'Unknown')}
              value={horses.find((h) => h.id === formData.horse_id) || null}
              onChange={(e, newValue) => {
                setFormData((prev) => ({ ...prev, horse_id: newValue?.id || '' }));
              }}
              renderInput={(params) => (
                <TextField size="small" {...params} label="Horse" required />
              )}
              noOptionsText={`No horses found (${horses.length} loaded)`}
            />

            {/* Feed Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                options={feedNames}
                getOptionLabel={(option) => option.name}
                filterOptions={createPrefixFilter((option) => option.name)}
                value={feedNames.find((n) => n.id === formData.feed_name_id) || null}
                onChange={handleFeedNameChange}
                renderInput={(params) => (
                  <TextField size="small" {...params} label="Feed Name" required />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feedBrands.find(b => b.id === option.brand_id)?.name || 'Unknown Brand'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {feedTypes.find(t => t.id === option.feed_type_id)?.name || 'Unknown Type'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                sx={{ flex: 1 }}
              />
              <IconButton
                onClick={() => setOpenFeedNameDialog(true)}
                sx={{
                  width: 56,
                  height: 56,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* Amount and Unit Row */}
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  size="small"
                  name="amount"
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={FEED_UNIT_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  filterOptions={createPrefixFilter((option) => option.label)}
                  value={FEED_UNIT_OPTIONS.find((option) => option.value === formData.unit) || null}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, unit: newValue?.value || '' }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Unit" />
                  )}
                />
              </Grid>
            </Grid>

            {/* Meal Time */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Meal Time
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="morning_feed"
                      checked={formData.morning_feed}
                      onChange={handleInputChange}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem',
                          strokeWidth: '1',
                        },
                      }}
                    />
                  }
                  label="Morning"
                  sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="midday_feed"
                      checked={formData.midday_feed}
                      onChange={handleInputChange}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem',
                          strokeWidth: '1',
                        },
                      }}
                    />
                  }
                  label="Midday"
                  sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="evening_feed"
                      checked={formData.evening_feed}
                      onChange={handleInputChange}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem',
                          strokeWidth: '1',
                        },
                      }}
                    />
                  }
                  label="Evening"
                  sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                />
              </Box>
            </Box>

            {/* Notes */}
            <TextField
              size="small"
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingFeed ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Feed Type Dialog */}
      <Dialog open={openFeedTypeDialog} onClose={() => setOpenFeedTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Feed Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              label="Name"
              value={feedTypeFormData.name}
              onChange={(e) => setFeedTypeFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              size="small"
              label="Description"
              value={feedTypeFormData.description}
              onChange={(e) => setFeedTypeFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeedTypeDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFeedType} variant="contained">Add Feed Type</Button>
        </DialogActions>
      </Dialog>

      {/* Add Feed Brand Dialog */}
      <Dialog open={openFeedBrandDialog} onClose={() => setOpenFeedBrandDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Feed Brand</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              label="Name"
              value={feedBrandFormData.name}
              onChange={(e) => setFeedBrandFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              size="small"
              label="Description"
              value={feedBrandFormData.description}
              onChange={(e) => setFeedBrandFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeedBrandDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFeedBrand} variant="contained">Add Feed Brand</Button>
        </DialogActions>
      </Dialog>

      {/* Add Feed Name Dialog */}
      <Dialog open={openFeedNameDialog} onClose={() => setOpenFeedNameDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Feed Name</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Feed Name */}
            <TextField
              size="small"
              label="Feed Name"
              value={feedNameFormData.name}
              onChange={(e) => setFeedNameFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />

            {/* Feed Type and Brand Row */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    options={feedTypes}
                    getOptionLabel={(option) => option.name}
                    filterOptions={createPrefixFilter((option) => option.name)}
                    value={feedTypes.find((t) => t.id === feedNameFormData.feed_type_id) || null}
                    onChange={(e, newValue) => {
                      const isHay = newValue?.name === 'Hay';
                      setFeedNameFormData(prev => ({ 
                        ...prev, 
                        feed_type_id: newValue?.id || '',
                        brand_id: isHay ? '' : prev.brand_id
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField size="small" {...params} label="Feed Type" required />
                    )}
                    forcePopupIcon={true}
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    onClick={() => setOpenFeedTypeDialog(true)}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      width: 56,
                      height: 56,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                        borderColor: 'primary.dark',
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  {(() => {
                    const selectedFeedType = feedTypes.find((t) => t.id === feedNameFormData.feed_type_id);
                    const isHay = selectedFeedType?.name === 'Hay';
                    return (
                      <>
                        <Autocomplete
                          options={feedBrands}
                          getOptionLabel={(option) => option.name}
                          filterOptions={createPrefixFilter((option) => option.name)}
                          value={feedBrands.find((b) => b.id === feedNameFormData.brand_id) || null}
                          onChange={(e, newValue) => 
                            setFeedNameFormData(prev => ({ ...prev, brand_id: newValue?.id || '' }))
                          }
                          renderInput={(params) => (
                            <TextField size="small" {...params} label="Brand" />
                          )}
                          forcePopupIcon={true}
                          disabled={isHay}
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton
                          onClick={() => {
                            setFeedBrandFormData(prev => ({ ...prev, feed_type_id: feedNameFormData.feed_type_id }));
                            setOpenFeedBrandDialog(true);
                          }}
                          disabled={isHay}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            width: 56,
                            height: 56,
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                              borderColor: 'primary.dark',
                            }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </>
                    );
                  })()}
                </Box>
              </Grid>
            </Grid>

            {/* Description */}
            <TextField
              size="small"
              label="Description"
              value={feedNameFormData.description}
              onChange={(e) => setFeedNameFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeedNameDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFeedName} variant="contained">Add Feed Name</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Feed Requirement"
        message="Permanently delete this feed requirement? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default FeedPage;

